import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Close, FilterList, NoteAdd } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { sortBy } from 'lodash';
import { isReviewItemActionable, pickNextPendingReviewItem } from 'pages/CapTable/cap-table/utilities/reviewAggregator';
import { isMappingSettleItem } from 'pages/CapTable/cap-table/utilities/settleMappingReview';
import type { ReviewItem, ReviewProvenanceFilter, ReviewStatusFilter } from 'store/useReviewStore';
import { cellRefMatches, reviewItemMatchesFilter, useReviewStore } from 'store/useReviewStore';
import ReviewCard from './ReviewCard';
import { hasReviewExcelJump } from './reviewDocJump';
import { ReviewSidePanelProps } from './ReviewSidePanel.props';
import {
  bulkActionButtonSx,
  bulkNoteFieldSx,
  clampReviewPanelWidth,
  DEFAULT_REVIEW_PANEL_WIDTH,
  emptyStateSx,
  filterButtonSx,
  filterMenuItemSx,
  filterSectionLabelSx,
  headerIconButtonSx,
  headerSx,
  headerTitleSx,
  listSx,
  panelSx,
  resizeHandleSx,
  sectionDividerSx,
  selectAllCheckboxSx,
  statusFilterSx,
} from './ReviewSidePanel.styles';

// Persisted across remounts (drawer close/reopen) within the session, mirroring
// the draggable drawer's module-level globalHeight.
let globalWidth = DEFAULT_REVIEW_PANEL_WIDTH;

/**
 * Persistent "Pending Review" side panel for the cap-table drawer: ONE mixed list
 * of every citation/reference on the table - AI-created and analyst-created alike
 * (SCAL-9159) - switchable through the status view filter (all / pending /
 * dismissed). Partitions the derived review items: PENDING cards (pinned top, full
 * inline detail + accept/decline + a multi-select checkbox on settleable items) and
 * REVIEWED/settled cards (bottom, muted + collapsed); within each section items
 * follow the default cap-table order. On accept/decline the item's status is
 * flipped in place so the card MOVES from the pending section to the reviewed
 * section instead of vanishing; clicking a reviewed card's status label REOPENS
 * it (SCAL-9262 undo), moving it back to pending with the controls re-presented.
 * When >=1 pending card is selected, compact
 * Accept/Decline bulk controls appear inline in the header (right-aligned), with
 * an opt-in collapsed note field under the header.
 */
const ReviewSidePanel = ({
  onAccept,
  onDecline,
  onSettleCitation,
  onSettleMapping,
  onAcceptValueRejectCitation,
  onReopen,
  onBulkSettle,
  onRevealPdf,
  onRevealExcel,
  documentNamesByRefId,
}: ReviewSidePanelProps) => {
  const reviewItems = useReviewStore(s => s.reviewItems);
  const selectedReviewCell = useReviewStore(s => s.selectedReviewCell);
  const selectedCardIds = useReviewStore(s => s.selectedCardIds);
  const filterSecurities = useReviewStore(s => s.filterSecurities);
  const filterMetrics = useReviewStore(s => s.filterMetrics);
  const filterCellIds = useReviewStore(s => s.filterCellIds);
  const filterStatus = useReviewStore(s => s.filterStatus);
  const filterProvenance = useReviewStore(s => s.filterProvenance);
  const setFilterStatus = useReviewStore(s => s.setFilterStatus);
  const setFilterProvenance = useReviewStore(s => s.setFilterProvenance);
  const setHoveredReviewCell = useReviewStore(s => s.setHoveredReviewCell);
  const setSelectedReviewCell = useReviewStore(s => s.setSelectedReviewCell);
  const setReviewItemStatus = useReviewStore(s => s.setReviewItemStatus);
  const toggleCardSelection = useReviewStore(s => s.toggleCardSelection);
  const selectCards = useReviewStore(s => s.selectCards);
  const clearSelection = useReviewStore(s => s.clearSelection);
  const toggleFilterSecurity = useReviewStore(s => s.toggleFilterSecurity);
  const toggleFilterMetric = useReviewStore(s => s.toggleFilterMetric);
  const clearReviewFilter = useReviewStore(s => s.clearReviewFilter);
  const setDrawerOpen = useReviewStore(s => s.setDrawerOpen);

  // Anchor for the single header filter menu (security + metric checkable lists).
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const filterActive = filterSecurities.size > 0 || filterMetrics.size > 0 || filterCellIds.size > 0;

  // SCAL-9150: this panel mounts ONLY while the drawer is open (DraggableDrawer renders
  // the side panel only when its content is visible), so its mount/unmount cleanly tracks
  // drawer-open. Publishing it to the store lets the grid's pending AI cells paint purple
  // while open. On close (this unmounts) we end review mode: drop the drawer-open flag AND
  // clear the selected/hovered cell so ReviewCellOutline stops outlining the grid - otherwise
  // the last-clicked card's AI-purple outline lingers after the drawer is gone.
  useEffect(() => {
    setDrawerOpen(true);
    return () => {
      setDrawerOpen(false);
      setSelectedReviewCell(null);
      setHoveredReviewCell(null);
    };
  }, [setDrawerOpen, setSelectedReviewCell, setHoveredReviewCell]);

  const [bulkReason, setBulkReason] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);

  // Drag-to-resize width, mirroring the draggable drawer's height drag: capture
  // the start X + width on mousedown, then track mousemove until mouseup. The
  // listeners go on the handle's OWN window (ownerDocument.defaultView), not the
  // global one: when the drawer is popped out, the panel lives in the popout
  // window and main-window listeners would never see the drag.
  const [width, setWidth] = useState(globalWidth);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const dragWindowRef = useRef<Window>(window);

  useEffect(() => {
    globalWidth = width;
  }, [width]);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      dragWindowRef.current = event.currentTarget.ownerDocument.defaultView ?? window;
      startXRef.current = event.clientX;
      startWidthRef.current = width;
      setIsResizing(true);
    },
    [width]
  );

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }
    const win = dragWindowRef.current;
    const onMove = (event: MouseEvent) =>
      setWidth(clampReviewPanelWidth(startWidthRef.current + (event.clientX - startXRef.current)));
    const onUp = () => setIsResizing(false);
    win.addEventListener('mousemove', onMove);
    win.addEventListener('mouseup', onUp);
    return () => {
      win.removeEventListener('mousemove', onMove);
      win.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  // Filter options come from every item, not just the visible ones, so picking one
  // never hides the others. Each list follows the grid: securities by column, metrics by row.
  const { securityOptions, metricOptions } = useMemo(() => {
    const labelsInGridOrder = (label: 'securityLabel' | 'fieldLabel', position: 'columnPosition' | 'rowPosition') =>
      Array.from(new Set(sortBy(reviewItems, position).map(i => i[label])));
    return {
      securityOptions: labelsInGridOrder('securityLabel', 'columnPosition'),
      metricOptions: labelsInGridOrder('fieldLabel', 'rowPosition'),
    };
  }, [reviewItems]);

  // ONE filter predicate (labels AND the status view filter) applied uniformly
  // before partitioning, so pending and reviewed sections always agree on what is
  // visible. Non-matching cards drop out of both lists, which is what scopes
  // "select all" to the visible set below.
  const { pending, reviewed } = useMemo(() => {
    const p: ReviewItem[] = [];
    const r: ReviewItem[] = [];
    reviewItems.forEach(i => {
      if (reviewItemMatchesFilter(i, filterSecurities, filterMetrics, filterStatus, filterProvenance, filterCellIds)) {
        (i.status === 'pending' ? p : r).push(i);
      }
    });
    return { pending: p, reviewed: r };
  }, [reviewItems, filterSecurities, filterMetrics, filterStatus, filterProvenance, filterCellIds]);

  // Only pending cards can be selected, so count membership against the (visible)
  // pending set; this is already filtered, so the bulk count reflects visible items.
  const selectedPending = useMemo(() => pending.filter(i => selectedCardIds.has(i.id)), [pending, selectedCardIds]);

  // Select-all targets currently-visible actionable pending cards - every card the
  // thumbs can settle (field, citation or AI Excel mapping).
  const visiblePendingIds = useMemo(() => pending.filter(isReviewItemActionable).map(i => i.id), [pending]);
  const allVisibleSelected = visiblePendingIds.length > 0 && visiblePendingIds.every(id => selectedCardIds.has(id));
  const someVisibleSelected = visiblePendingIds.some(id => selectedCardIds.has(id));

  const handleSelectAllVisible = useCallback(
    () => (allVisibleSelected ? clearSelection() : selectCards(visiblePendingIds)),
    [allVisibleSelected, clearSelection, selectCards, visiblePendingIds]
  );

  const docNameFor = useCallback(
    (item: ReviewItem) =>
      item.primaryCitation?.document_reference != null
        ? documentNamesByRefId?.get(item.primaryCitation.document_reference)
        : undefined,
    [documentNamesByRefId]
  );

  // Hovering a card outlines its grid cell (and reveals the merge text) only while
  // hovered; clicking makes it the single active card (persistent outline + border).
  const handleHoverChange = useCallback(
    (item: ReviewItem, hovered: boolean) =>
      setHoveredReviewCell(hovered ? { securityId: item.securityId, fieldAlias: item.fieldAlias } : null),
    [setHoveredReviewCell]
  );

  // Stable identity so memoized cards don't all re-render when one is (de)selected.
  const handleToggleSelect = useCallback((item: ReviewItem) => toggleCardSelection(item.id), [toggleCardSelection]);

  const handleActivate = useCallback(
    (item: ReviewItem) => {
      const ref = { securityId: item.securityId, fieldAlias: item.fieldAlias };
      // Toggle: clicking the already-active card clears the active selection. The
      // PDF jump has MOVED to the card's eyeball (onRevealPdf); body activation no
      // longer opens any document.
      const becomingActive = !cellRefMatches(selectedReviewCell, ref);
      setSelectedReviewCell(becomingActive ? ref : null);
      // Feature C: a card body becoming active reveals its Excel mapping-reference
      // source (xlsx viewer -> sheet + highlight). NO-OP for items without an Excel
      // mapping reference (handled by the drawer's buildReviewExcelJump null guard).
      if (becomingActive) {
        onRevealExcel?.(item);
      }
    },
    [selectedReviewCell, setSelectedReviewCell, onRevealExcel]
  );

  const advanceAfterSettle = useCallback(
    (settledId: string) => {
      const next = pickNextPendingReviewItem(pending, settledId);
      if (!next) {
        return;
      }
      setSelectedReviewCell({ securityId: next.securityId, fieldAlias: next.fieldAlias });
      if (hasReviewExcelJump(next)) {
        onRevealExcel?.(next);
      } else {
        onRevealPdf?.(next);
      }
    },
    [onRevealExcel, onRevealPdf, pending, setSelectedReviewCell]
  );

  // Accept/decline now MOVE the card: persist, then flip its status in place so it
  // re-renders in the muted Reviewed section. A failed request leaves it pending.
  // A field-settleable card persists through onAccept/onDecline; a reference-only
  // card (canSettleCitation) settles its ACTIVE citation instead (SCAL-8963), and an
  // Excel-mapping-only card settles its AI mapping rows (SCAL-9667).
  const handleAccept = useCallback(
    async (item: ReviewItem, reason?: string) => {
      try {
        if (item.canSettle) {
          await onAccept?.(item.securityId, item.fieldAlias, reason);
          setReviewItemStatus(item.id, 'accepted');
        } else if (isMappingSettleItem(item)) {
          await onSettleMapping?.(item, 'accept');
          setReviewItemStatus(item.id, 'accepted');
        } else {
          await onSettleCitation?.(item, 'accept');
          // The citation handler derives the card from all remaining refs.
          if (useReviewStore.getState().reviewItems.find(current => current.id === item.id)?.status === 'pending') {
            return;
          }
        }
        advanceAfterSettle(item.id);
      } catch {
        // Keep the item pending if the request failed; the handler surfaces the error.
      }
    },
    [advanceAfterSettle, onAccept, onSettleCitation, onSettleMapping, setReviewItemStatus]
  );

  const handleDecline = useCallback(
    async (item: ReviewItem, reason?: string) => {
      try {
        if (item.canSettle) {
          await onDecline?.(item.securityId, item.fieldAlias, reason);
          setReviewItemStatus(item.id, 'dismissed');
        } else if (isMappingSettleItem(item)) {
          await onSettleMapping?.(item, 'dismiss');
          setReviewItemStatus(item.id, 'dismissed');
        } else {
          await onSettleCitation?.(item, 'dismiss');
          // The citation handler derives the card from all remaining refs.
          if (useReviewStore.getState().reviewItems.find(current => current.id === item.id)?.status === 'pending') {
            return;
          }
        }
        advanceAfterSettle(item.id);
      } catch {
        // Keep the item pending if the request failed.
      }
    },
    [advanceAfterSettle, onDecline, onSettleCitation, onSettleMapping, setReviewItemStatus]
  );

  const handleAcceptValueRejectCitation = useCallback(
    async (item: ReviewItem) => {
      try {
        await onAcceptValueRejectCitation?.(item);
        setReviewItemStatus(item.id, 'accepted');
        advanceAfterSettle(item.id);
      } catch {
        // Keep the item pending if the request failed.
      }
    },
    [advanceAfterSettle, onAcceptValueRejectCitation, setReviewItemStatus]
  );

  // SCAL-9262: undo a decision - the reviewed card's status label reopens the
  // item, MOVING it back to the pending section where the accept/decline
  // controls live again. When the un-accept kept a differing manual value
  // (never clobbered), the reopened card is flagged via `valueKept`. A failed
  // request leaves the card settled.
  const handleReopen = useCallback(
    async (item: ReviewItem) => {
      try {
        if (isMappingSettleItem(item)) {
          // A mapping settle never wrote a value, so there is none to keep.
          await onSettleMapping?.(item, 'reopen');
          setReviewItemStatus(item.id, 'pending');
          return;
        }
        const result = await onReopen?.(item);
        setReviewItemStatus(item.id, 'pending', result?.valueKept || undefined);
      } catch {
        // Keep the item settled if the request failed; the handler surfaces the error.
      }
    },
    [onReopen, onSettleMapping, setReviewItemStatus]
  );

  // OPTIMISTIC bulk settle: move every selected card to the reviewed section and
  // clear the selection IMMEDIATELY (so the header controls vanish and the cards
  // slide down at once), then fire the batch request in the background. On the
  // response, revert any optimistically-moved id the server did NOT settle back to
  // pending; if the whole request throws, revert all of them. `movedItems` is
  // captured up-front so reconciliation targets exactly the cards we moved.
  const handleBulk = useCallback(
    async (action: 'accept' | 'decline') => {
      if (!onBulkSettle || selectedPending.length === 0) {
        return;
      }
      const movedItems = selectedPending;
      const settledStatus = action === 'accept' ? 'accepted' : 'dismissed';
      const reason = bulkReason.trim() || undefined;

      movedItems.forEach(i => setReviewItemStatus(i.id, settledStatus));
      setBulkReason('');
      setNoteOpen(false);
      clearSelection();

      try {
        const { settledIds } = await onBulkSettle(action, movedItems, reason);
        const settled = new Set(settledIds);
        movedItems.forEach(i => (settled.has(i.id) ? undefined : setReviewItemStatus(i.id, 'pending')));
      } catch {
        // Whole request failed: revert every optimistically-moved card to pending.
        // The error toast is surfaced by the drawer's onBulkSettle handler.
        movedItems.forEach(i => setReviewItemStatus(i.id, 'pending'));
      }
    },
    [onBulkSettle, selectedPending, bulkReason, setReviewItemStatus, clearSelection]
  );

  return (
    <Box sx={panelSx} style={{ width }} data-testid="review-side-panel">
      <Box sx={headerSx}>
        <Typography sx={headerTitleSx}>Pending Review:</Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {selectedPending.length > 0 && (
            <Stack direction="row" alignItems="center" gap={0.5} data-testid="review-bulk-controls">
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={bulkActionButtonSx('accept')}
                onClick={() => handleBulk('accept')}>
                Accept ({selectedPending.length})
              </Button>
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={bulkActionButtonSx('decline')}
                onClick={() => handleBulk('decline')}>
                Decline ({selectedPending.length})
              </Button>
              <Tooltip title={noteOpen ? 'Hide bulk note' : 'Add bulk note'}>
                <IconButton
                  size="small"
                  aria-label="toggle bulk note"
                  aria-expanded={noteOpen}
                  sx={headerIconButtonSx}
                  onClick={() => setNoteOpen(o => !o)}>
                  <NoteAdd fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear selection">
                <IconButton
                  size="small"
                  aria-label="clear selection"
                  sx={headerIconButtonSx}
                  onClick={() => {
                    setNoteOpen(false);
                    clearSelection();
                  }}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          {visiblePendingIds.length > 0 && (
            <Tooltip title={allVisibleSelected ? 'Clear selection' : 'Select all shown'}>
              <Checkbox
                size="small"
                data-testid="review-select-all"
                sx={selectAllCheckboxSx}
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                slotProps={{ input: { 'aria-label': 'select all shown' } }}
                onChange={handleSelectAllVisible}
              />
            </Tooltip>
          )}
          {reviewItems.length > 0 && (
            <Tooltip title={filterActive ? 'Filter (active)' : 'Filter'}>
              <IconButton
                size="small"
                aria-label="filter review items"
                aria-expanded={Boolean(filterAnchor)}
                sx={filterButtonSx(filterActive)}
                onClick={e => setFilterAnchor(e.currentTarget)}>
                <FilterList fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
      {/* SCAL-9159 status VIEW filter: one mixed list of every citation/reference,
          switchable between all / pending / dismissed. Always visible (unlike the
          security/metric funnel menu) so the current view is never ambiguous. */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filterStatus}
        aria-label="review status filter"
        data-testid="review-status-filter"
        sx={statusFilterSx}
        onChange={(_, value: ReviewStatusFilter | null) => value && setFilterStatus(value)}>
        <ToggleButton value="all">All</ToggleButton>
        <ToggleButton value="pending">Pending</ToggleButton>
        <ToggleButton value="dismissed">Dismissed</ToggleButton>
      </ToggleButtonGroup>
      {/* Provenance VIEW filter (user-ratified): default AI - analysts review AI
          output; Manual/All widen the view. ANDs with the status filter above.
          PENDING items always show regardless (reviewItemMatchesFilter). */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={filterProvenance}
        aria-label="review provenance filter"
        data-testid="review-provenance-filter"
        sx={statusFilterSx}
        onChange={(_, value: ReviewProvenanceFilter | null) => value && setFilterProvenance(value)}>
        <ToggleButton value="ai">AI</ToggleButton>
        <ToggleButton value="manual">Manual</ToggleButton>
        <ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Typography sx={filterSectionLabelSx}>Security</Typography>
        {securityOptions.map(label => (
          <MenuItem key={`sec-${label}`} dense sx={filterMenuItemSx} onClick={() => toggleFilterSecurity(label)}>
            <Checkbox size="small" disableRipple tabIndex={-1} checked={filterSecurities.has(label)} />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
        <Typography sx={filterSectionLabelSx}>Metric</Typography>
        {metricOptions.map(label => (
          <MenuItem key={`metric-${label}`} dense sx={filterMenuItemSx} onClick={() => toggleFilterMetric(label)}>
            <Checkbox size="small" disableRipple tabIndex={-1} checked={filterMetrics.has(label)} />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
        {filterActive && (
          <MenuItem dense sx={filterMenuItemSx} onClick={clearReviewFilter}>
            <ListItemText primary="Clear filter" />
          </MenuItem>
        )}
      </Menu>
      <Collapse in={selectedPending.length > 0 && noteOpen} unmountOnExit>
        <TextField
          fullWidth
          multiline
          size="small"
          variant="standard"
          placeholder="Optional note for the selected items"
          value={bulkReason}
          sx={bulkNoteFieldSx}
          onChange={e => setBulkReason(e.target.value)}
        />
      </Collapse>
      {reviewItems.length === 0 ? (
        <Typography sx={emptyStateSx}>No items to review.</Typography>
      ) : (
        <Box sx={listSx} data-testid="review-panel-list">
          {/* The data-testid above doubles as the card scroll's viewport anchor (the
              settle guard's containerSelector) - keep it on the SCROLLING box. */}
          {/* The pending empty-state only belongs to views that SHOW pending items;
              the dismissed-only view gets its own wording instead. */}
          {pending.length === 0 && filterStatus !== 'dismissed' && (
            <Typography sx={emptyStateSx}>
              {filterActive || filterStatus === 'pending'
                ? 'No pending items match the filter.'
                : 'All suggestions reviewed.'}
            </Typography>
          )}
          {filterStatus === 'dismissed' && reviewed.length === 0 && (
            <Typography sx={emptyStateSx}>No dismissed items.</Typography>
          )}
          {pending.map(item => (
            <ReviewCard
              key={item.id}
              item={item}
              active={cellRefMatches(selectedReviewCell, {
                securityId: item.securityId,
                fieldAlias: item.fieldAlias,
              })}
              selected={selectedCardIds.has(item.id)}
              documentName={docNameFor(item)}
              onAccept={handleAccept}
              onDecline={handleDecline}
              onAcceptValueRejectCitation={handleAcceptValueRejectCitation}
              onToggleSelect={handleToggleSelect}
              onActivate={handleActivate}
              onHoverChange={handleHoverChange}
              onRevealPdf={onRevealPdf}
              onRevealExcel={onRevealExcel}
            />
          ))}

          {reviewed.length > 0 && (
            <>
              <Typography sx={sectionDividerSx}>Reviewed ({reviewed.length})</Typography>
              {reviewed.map(item => (
                <ReviewCard
                  key={item.id}
                  item={item}
                  reviewed
                  active={cellRefMatches(selectedReviewCell, {
                    securityId: item.securityId,
                    fieldAlias: item.fieldAlias,
                  })}
                  documentName={docNameFor(item)}
                  onActivate={handleActivate}
                  onHoverChange={handleHoverChange}
                  onRevealPdf={onRevealPdf}
                  onRevealExcel={onRevealExcel}
                  onReopen={handleReopen}
                />
              ))}
            </>
          )}
        </Box>
      )}
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="resize review panel"
        data-testid="review-panel-resize-handle"
        sx={resizeHandleSx}
        onMouseDown={handleResizeStart}
      />
    </Box>
  );
};

export default ReviewSidePanel;
